<template>
  <section
    v-if="fields && cards.length"
    :class="rootClass"
    :style="rootStyle"
    :id="rendering?.dataSource"
  >
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div
        v-if="fields?.title?.value || fields?.description?.value"
        class="c-featured-content-cards__header"
      >
        <h2
          v-if="fields?.title?.value"
          class="c-featured-content-cards__heading"
          v-html="fields.title.value"
        />
        <RichText
          v-if="fields?.description?.value"
          class="c-featured-content-cards__intro"
          :field="fields.description"
        />
      </div>

      <div ref="mobileTrackEl" class="c-featured-content-cards__grid" @scroll="updateActiveCard">
        <article v-for="card in cards" :key="card.id" class="c-featured-content-cards__card">
          <component
            v-if="card.hasMedia"
            :is="card.href ? 'a' : 'div'"
            class="c-featured-content-cards__media-link"
            :href="card.href || undefined"
            target="_blank"
            :aria-label="card.linkLabel"
          >
            <span
              v-if="card.label"
              class="c-featured-content-cards__label"
              :style="card.labelStyle"
            >
              {{ card.label }}
            </span>
            <AdaptiveImage
              v-if="card.fields.image"
              class="c-featured-content-cards__image"
              :field="card.fields.image"
              :show="true"
              :enable-scroll-detection="false"
              :show-image-redius="false"
            />
          </component>

          <div class="c-featured-content-cards__content">
            <RichText
              v-if="card.fields.title?.value"
              class="c-featured-content-cards__title"
              :field="card.fields.title"
              tag="h3"
            />
            <RichText
              v-if="card.fields.description?.value"
              class="c-featured-content-cards__description"
              :field="card.fields.description"
            />
            <a
              v-if="card.linkText"
              class="c-featured-content-cards__link"
              :href="card.href"
              target="_blank"
            >
              <span>{{ card.linkText }}</span>
              <span class="c-featured-content-cards__arrow" aria-hidden="true"></span>
            </a>
          </div>
        </article>
      </div>

      <div v-if="cards.length > 1" class="c-featured-content-cards__pagination" aria-hidden="true">
        <button
          v-for="(_, index) in cards"
          :key="`featured-content-card-pagination-${index}`"
          type="button"
          class="c-featured-content-cards__pagination-item"
          :class="{ 'is-active': activeCardIndex === index }"
          tabindex="-1"
          @click="scrollToCard(index)"
        ></button>
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RichText, Text as JssText } from '@sitecore-jss/sitecore-jss-vue';
import type {
  FeaturedContentCardsItemFields,
  FeaturedContentCardsProps,
} from '@/types/components/FeaturedContentCards';
import AdaptiveImage from '@/elements/AdaptiveImage.vue';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

interface NormalizedCard extends ScItem<FeaturedContentCardsItemFields> {
  fields: FeaturedContentCardsItemFields;
  label: string;
  labelStyle: Record<string, string>;
  href: string;
  target?: string;
  linkText: string;
  linkLabel: string;
  showLink: boolean;
  hasMedia: boolean;
}

const props = defineProps<FeaturedContentCardsProps>();
const fields = computed(() => props.fields);
const mobileTrackEl = ref<HTMLElement | null>(null);
const activeCardIndex = ref(0);
const titleHeight = ref(0);
let titleResizeObserver: ResizeObserver | null = null;

const rootClass = computed(() =>
  utils.overall.baseComClass(props.fields, 'c-featured-content-cards')
);
const rootStyle = computed(() => ({
  ...(utils.overall?.baseComStyle?.(fields.value) || {}),
  '--featured-card-title-height': titleHeight.value ? `${titleHeight.value}px` : 'auto',
}));

const rawCards = computed<ScItem<FeaturedContentCardsItemFields>[]>(() => {
  const cardItems = utils.sc.getFieldObj(fields.value, 'cards');
  return Array.isArray(cardItems) ? cardItems : [];
});

const cards = computed<NormalizedCard[]>(() =>
  rawCards.value
    .map((card, index) => {
      const cardFields = card.fields;
      if (!cardFields) return null;

      const linkValue = cardFields.link?.value || {};
      const title = cardFields.title?.value || `Featured content ${index + 1}`;
      const labelColor = cardFields.labelTone?.value || '#efd196';

      return {
        ...card,
        fields: cardFields,
        label: cardFields.label?.value || '',
        labelStyle: getLabelStyle(labelColor),
        href: linkValue.href || '',
        target: linkValue.target,
        linkText: cardFields.linkText?.value,
        linkLabel: linkValue.title || cardFields.linkText?.value || linkValue.text || title,
        showLink: Boolean(linkValue.href),
        hasMedia: Boolean(
          cardFields.image?.fields?.desktopImage?.value?.src ||
          cardFields.image?.fields?.mobileImage?.value?.src
        ),
      };
    })
    .filter((card): card is NormalizedCard => Boolean(card))
);

const getLabelStyle = (backgroundColor: string) => ({
  backgroundColor,
  color: shouldUseLightText(backgroundColor) ? '#fff' : '#454545',
});

const shouldUseLightText = (color: string) => {
  const hex = color.trim().replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(hex)) return false;

  const red = parseInt(hex.slice(0, 2), 16);
  const green = parseInt(hex.slice(2, 4), 16);
  const blue = parseInt(hex.slice(4, 6), 16);
  return (red * 299 + green * 587 + blue * 114) / 1000 < 145;
};

const updateActiveCard = () => {
  const track = mobileTrackEl.value;
  if (!track) return;

  const card = track.querySelector<HTMLElement>('.c-featured-content-cards__card');
  if (!card) return;

  const gap = parseFloat(window.getComputedStyle(track).columnGap || '0');
  const step = card.offsetWidth + gap;
  activeCardIndex.value = Math.min(
    cards.value.length - 1,
    Math.max(0, Math.round(track.scrollLeft / step))
  );
};

const scrollToCard = (index: number) => {
  const track = mobileTrackEl.value;
  if (!track) return;

  const card = track.querySelector<HTMLElement>('.c-featured-content-cards__card');
  if (!card) return;

  const gap = parseFloat(window.getComputedStyle(track).columnGap || '0');
  track.scrollTo({
    left: index * (card.offsetWidth + gap),
    behavior: 'smooth',
  });
};

const syncTitleHeight = () => {
  const grid = mobileTrackEl.value;
  if (!grid || window.innerWidth < 744) {
    titleHeight.value = 0;
    return;
  }

  titleHeight.value = Math.ceil(
    Math.max(
      0,
      ...Array.from(grid.querySelectorAll<HTMLElement>('.c-featured-content-cards__title')).map(
        (title) => title.getBoundingClientRect().height
      )
    )
  );
};

onMounted(async () => {
  await nextTick();
  syncTitleHeight();

  if (window.ResizeObserver && mobileTrackEl.value) {
    titleResizeObserver = new ResizeObserver(syncTitleHeight);
    titleResizeObserver.observe(mobileTrackEl.value);
  }
});

watch(
  () => cards.value.length,
  () => nextTick(syncTitleHeight)
);

onBeforeUnmount(() => {
  titleResizeObserver?.disconnect();
});
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-featured-content-cards {
  background: $white;

  &__header {
    max-width: 880px;
    margin: 0 auto 32px;
    text-align: center;
  }

  &__heading {
    margin: 0;
    color: #050505;
    font-size: 30px;
    line-height: 1.35;
    font-weight: 600;
    letter-spacing: 0;
  }

  &__intro {
    margin: 0;
    color: #686868;
    font-size: 16px;
    line-height: 1.5;

    p {
      margin: 0;
    }
  }

  &__heading + &__intro {
    margin-top: 20px;
  }

  &__grid {
    display: flex;
    gap: 24px;
    max-width: 1440px;
    margin: 0 auto;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__card {
    flex: 0 0 100%;
    min-width: 0;
    scroll-snap-align: start;
  }

  &__media-link {
    position: relative;
    display: block;
    overflow: hidden;
    border-radius: 14px;
    aspect-ratio: 1.5 / 1;
    color: inherit;
    background: #e8eee9;
    text-decoration: none;
  }

  &__image {
    width: 100%;
    height: 100%;
    transition: transform 0.45s ease;
  }

  &__image,
  &__image > div,
  &__image .e-background-image,
  &__image .e-background-image__img,
  &__image picture,
  &__image img,
  &__image .background-image {
    display: block;
    width: 100%;
    height: 100%;
  }

  &__image img {
    object-fit: cover;
  }

  &__media-link:hover &__image {
    transform: scale(1.045);
  }

  &__label {
    position: absolute;
    top: 22px;
    left: 20px;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 112px;
    min-height: 34px;
    padding: 6px 20px;
    border-radius: 999px;
    color: #454545;
    font-size: 16px;
    line-height: 1;
    font-weight: 400;
  }

  &__content {
    padding-top: 20px;
  }

  &__title {
    margin: 0;
    color: #3f3f3f;
    font-size: 25px;
    line-height: 1.38;
    font-weight: 400;
    letter-spacing: 0;
  }

  &__description {
    margin-top: 16px;
    color: #686868;
    font-size: 19px;
    line-height: 1.42;

    p {
      margin: 0;
    }
  }

  &__link {
    display: inline-flex;
    align-items: center;
    gap: 18px;
    margin-top: 28px;
    color: #719472;
    font-size: 19px;
    line-height: 1.2;
    text-decoration: none;
  }

  &__link:visited,
  &__link:hover,
  &__link:active,
  &__link:focus {
    color: #719472;
    text-decoration: none;
  }

  &__arrow {
    width: 12px;
    height: 12px;
    border-top: 1px solid currentColor;
    border-right: 1px solid currentColor;
    transform: rotate(45deg);
    transition: transform 0.2s ease;
  }

  &__link:hover &__arrow {
    transform: translateX(3px) rotate(45deg);
  }

  &__pagination {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 38px;
  }

  &__pagination-item {
    display: block;
    width: 36px;
    height: 4px;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: rgba(113, 148, 114, 0.35);
    cursor: pointer;

    &.is-active {
      width: 54px;
      background: #719472;
    }
  }

  @include tablet {
    &__header {
      margin-bottom: 36px;
    }

    &__heading {
      font-size: 36px;
    }

    &__grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 40px;
      overflow: visible;
      scroll-snap-type: none;
    }

    &__card {
      flex: initial;
      scroll-snap-align: none;
    }

    &__label {
      top: 20px;
      left: 20px;
      min-width: 76px;
      padding: 6px 18px;
    }

    &__title {
      min-height: var(--featured-card-title-height);
      font-size: 20px;
      line-height: 1.28;
    }

    &__description {
      font-size: 16px;
    }

    &__link {
      gap: 6px;
      margin-top: 16px;
      font-size: 14px;
    }

    &__arrow {
      width: 8px;
      height: 8px;
    }

    &__pagination {
      display: none;
    }
  }

  @include tablet-landscape {
    &__heading {
      font-size: 38px;
    }

    &__grid {
      gap: 64px;
    }

    &__content {
      padding-top: 20px;
    }
  }
}
</style>
